<script setup lang="ts">
import type { AppSchemasTermTerm as Term } from "@classes-service-types/index.ts";
import type { TodosSubmissionsStatus } from "@profile-service-types/index.ts";
import { format, parseISO } from "date-fns";
import { computed, inject, toRefs } from "vue";
import StatusSelect from "@/components/MyStudents/Submissions/components/StatusSelect/index.vue";
import { TODO_SUBMISSION_STATUS_KEY } from "@/components/MyStudents/Submissions/composables/useTodoSubmissionStatus";
import { ARCHIVED_TOOLTIP_MESSAGE, READ_ONLY_TOOLTIP_MESSAGE } from "@/components/MyStudents/Submissions/constants";
import type { DisplayableStatus } from "@/components/MyStudents/Submissions/statusMaps";
import { ARCHIVED_STATUS } from "@/components/MyStudents/Submissions/statusMaps";
import LockBadge from "@/components/shared/Badge/LockBadge.vue";
import { hasMyStudentsToDosEnabled, hasTodoUpdateSubmissionPermission } from "@/composables/authorization.js";
import type { SubmissionHeaderProps } from "./types";

const props = defineProps<SubmissionHeaderProps>();

const todoSubmissionStatus = inject(TODO_SUBMISSION_STATUS_KEY, undefined);

const showStatusControl = computed<boolean>(() => !!hasMyStudentsToDosEnabled.value && !!todoSubmissionStatus);
const permittedStatuses = computed<TodosSubmissionsStatus[]>(
  () => todoSubmissionStatus?.permittedStatuses.value ?? [],
);

const isTodoArchived = computed<boolean>(() => !!todoSubmissionStatus?.isTodoArchived.value);
const isReadOnly = computed<boolean>(() => !hasTodoUpdateSubmissionPermission.value);
const isSelectDisabled = computed<boolean>(() => isTodoArchived.value || isReadOnly.value);
const isZeroState = computed<boolean>(() => !todoSubmissionStatus?.hasSubmission.value);

const selectValue = computed<DisplayableStatus | undefined>(() =>
  isTodoArchived.value ? ARCHIVED_STATUS : todoSubmissionStatus?.displayedStatus.value,
);

const lockBadge = computed<{ text: string; tooltip: string } | undefined>(() => {
  if (isTodoArchived.value) {
    return { text: "Archived", tooltip: ARCHIVED_TOOLTIP_MESSAGE };
  }
  if (isReadOnly.value) {
    return { text: "Read only", tooltip: READ_ONLY_TOOLTIP_MESSAGE };
  }
  return undefined;
});

const archivedTooltipConfig = computed(() => ({
  value: ARCHIVED_TOOLTIP_MESSAGE,
  disabled: !isTodoArchived.value,
  escape: true,
  class: "bg-white max-w-xs shaddow rounded white",
  hideDelay: "0.3",
  pt: {
    text: "text-indigo-base p-3 leading-none",
  },
}));

const onStatusChange = (status: TodosSubmissionsStatus): void => {
  todoSubmissionStatus?.selectStatus(status);
};

const { participantName, todoTitle, programData, sessionData, dueDate } = toRefs(props);

const formattedDueDate = computed(() =>
  dueDate.value ? format(parseISO(dueDate.value as string), "MMM dd, yyyy") : "",
);

const programName = computed(() => programData.value?.name ?? "");
const sessionCity = computed(() => {
  const arrivalCity = sessionData.value?.session_travel_details?.[0]?.arrival_city;
  return arrivalCity?.city ?? "";
});
const sessionTerms = computed(() => {
  const terms = (sessionData.value?.terms as Term[]) ?? [];
  return terms.map((term: Term) => term.name);
});
const sessionAcademicYear = computed(() => sessionData.value?.academic_year ?? "");

const subtitle = computed(() => {
  const terms = sessionTerms.value.join("-");
  const subtitleParts = [sessionCity.value, terms, sessionAcademicYear.value, programName.value].filter(Boolean);
  return subtitleParts.join("-");
});
</script>

<template>
  <div
    class="bg-blue-400 border border-purple-700 rounded-lg py-5 px-8 flex flex-wrap justify-between gap-4"
    data-testid="my-students-header"
  >
    <div class="flex flex-col gap-4">
      <div>
        <div class="text-xl font-bold text-indigo-base">{{ participantName }}'s {{ todoTitle }}</div>
        <div class="mt-1 text-xs font-semibold text-indigo-base opacity-70 uppercase">{{ subtitle }}</div>
      </div>
      <div v-if="formattedDueDate" class="flex flex-wrap gap-12">
        <div class="flex flex-col gap-0.5 min-w-0">
          <span class="text-13p font-bold text-indigo-base">Due Date</span>
          <span class="text-13p font-normal text-indigo-base">{{ formattedDueDate }}</span>
        </div>
      </div>
    </div>
    <div
      v-if="showStatusControl"
      class="flex items-center gap-2.5 self-start"
      data-testid="my-students-status-control"
    >
      <span class="text-sm font-bold" :class="isReadOnly ? 'text-gray-600' : 'text-indigo-base'">To-Do Status</span>
      <LockBadge v-if="lockBadge" :text="lockBadge.text" :tooltip="lockBadge.tooltip" />
      <span v-tooltip.top="archivedTooltipConfig">
        <StatusSelect
          :model-value="selectValue"
          :permitted-statuses="permittedStatuses"
          :disabled="isSelectDisabled"
          :zero-state="isZeroState"
          @update:model-value="onStatusChange"
        />
      </span>
    </div>
  </div>
</template>
